// "Top contributors": the people adding the most ideas, prompts, agents and
// apps, from AI Initiatives and the AI Library. Champions are marked.
import { useCallback, useMemo, useState } from "react";
import { Award, Heart, HelpCircle, Rocket, Trophy } from "lucide-react";
import { useContributions } from "../../hooks/useContributions";
import { usePeoplePhotos } from "../../hooks/usePeoplePhotos";
import { quarterStart, rankContributors, withoutExcluded, type Contributor, type Exclusions, type Window } from "../../lib/contributions";
import type { ChampionProfile } from "../../hooks/useChampionDirectory";
import { Face } from "./parts";
import { PointsGuide } from "./PointsGuide";

const SHOWN = 10;

function Breakdown({ c }: { c: Contributor }) {
  const parts: { label: string; n: number }[] = [
    { label: c.ideas === 1 ? "idea" : "ideas", n: c.ideas },
    { label: c.tools.Prompt === 1 ? "prompt" : "prompts", n: c.tools.Prompt },
    { label: c.tools.Agent === 1 ? "agent" : "agents", n: c.tools.Agent },
    { label: c.tools.App === 1 ? "app" : "apps", n: c.tools.App },
    { label: c.tools.Other === 1 ? "tool" : "tools", n: c.tools.Other },
  ].filter((p) => p.n);
  return (
    <span className="tc-breakdown">
      {parts.map((p) => (
        <span key={p.label} className="tc-chip">
          {p.n} {p.label}
        </span>
      ))}
      {c.live ? (
        <span className="tc-chip tc-chip--live">
          <Rocket size={11} aria-hidden /> {c.live} live
        </span>
      ) : null}
      {c.likes ? (
        <span className="tc-chip tc-chip--likes">
          <Heart size={11} aria-hidden /> {c.likes}
        </span>
      ) : null}
    </span>
  );
}

export function TopContributors({
  champions,
  viewerEmail,
  exclude,
}: {
  champions: ChampionProfile[];
  viewerEmail?: string | null;
  /** People left out of the ranking (see Champion admin → Program). */
  exclude: Exclusions;
}) {
  const { data, error } = useContributions();
  const [period, setPeriod] = useState<Window>("all");
  const [guide, setGuide] = useState(false);
  const closeGuide = useCallback(() => setGuide(false), []);
  // The viewer's own all-time points, to show where they are in the guide.
  const myPoints = useMemo(() => {
    if (!data || !viewerEmail) return undefined;
    return rankContributors(data.initiatives, data.library).find((c) => c.email === viewerEmail.toLowerCase())?.points ?? 0;
  }, [data, viewerEmail]);

  const ranked = useMemo(
    () =>
      data
        ? withoutExcluded(rankContributors(data.initiatives, data.library, period === "quarter" ? quarterStart() : null), exclude)
        : [],
    [data, period, exclude]
  );
  const top = ranked.slice(0, SHOWN);
  const photos = usePeoplePhotos(top.map((c) => c.email));

  const championFor = (c: Contributor) =>
    champions.find(
      (p) => (c.email && p.email?.toLowerCase() === c.email) || p.name.toLowerCase() === c.name.toLowerCase()
    );
  const photoFor = (c: Contributor) => championFor(c)?.photoUrl ?? photos[c.email] ?? null;
  const best = top[0]?.points || 1;

  return (
    <section className="db-card tc" aria-labelledby="tc-title">
      <div className="db-section-head">
        <div>
          <h2 id="tc-title" className="db-section-title">
            Top contributors
          </h2>
          <p className="db-section-sub">The people adding the most ideas, prompts, agents and apps. Thank you!</p>
        </div>
        <div className="tc-tools">
        <button type="button" className="tc-guide-btn" onClick={() => setGuide(true)}>
          <HelpCircle size={15} aria-hidden /> How points work
        </button>
        <div className="tc-window" role="group" aria-label="Time period">
          {(["quarter", "all"] as Window[]).map((w) => (
            <button key={w} type="button" className={period === w ? "is-active" : ""} aria-pressed={period === w} onClick={() => setPeriod(w)}>
              {w === "quarter" ? "This quarter" : "All time"}
            </button>
          ))}
        </div>
        </div>
      </div>

      {error ? (
        <p className="db-empty-text">Couldn't load contributions right now.</p>
      ) : !data ? (
        <div className="db-skel-rows" aria-busy="true">
          <span />
          <span />
          <span />
        </div>
      ) : !top.length ? (
        <p className="db-empty-text">Nothing added {period === "quarter" ? "this quarter" : "yet"}. Be the first!</p>
      ) : (
        <>
          <ol className="tc-podium">
            {top.slice(0, 3).map((c, i) => {
              const champ = championFor(c);
              return (
                <li key={c.key} className={`tc-place tc-place--${i + 1}`}>
                  <span className="tc-medal" aria-label={`Number ${i + 1}`}>
                    {i === 0 ? <Trophy size={14} aria-hidden /> : i + 1}
                  </span>
                  <Face name={c.name} photoUrl={photoFor(c)} size={i === 0 ? 64 : 56} />
                  <span className="tc-name">{c.name}</span>
                  {champ ? (
                    <span className="tc-champ">
                      <Award size={11} aria-hidden /> AI Champion
                    </span>
                  ) : null}
                  <span className="tc-points">{c.points} pts</span>
                  <Breakdown c={c} />
                </li>
              );
            })}
          </ol>

          {top.length > 3 ? (
            <ol className="tc-list" start={4}>
              {top.slice(3).map((c, i) => (
                <li key={c.key} className="tc-row">
                  <span className="tc-rank">{i + 4}</span>
                  <Face name={c.name} photoUrl={photoFor(c)} size={34} />
                  <span className="tc-row-text">
                    <span className="tc-row-name">
                      {c.name}
                      {championFor(c) ? (
                        <span className="tc-champ" title="AI Champion">
                          <Award size={11} aria-hidden /> Champion
                        </span>
                      ) : null}
                    </span>
                    <Breakdown c={c} />
                  </span>
                  <span className="tc-bar" aria-hidden>
                    <span style={{ width: `${Math.max(6, (c.points / best) * 100)}%` }} />
                  </span>
                  <span className="tc-row-points">{c.points}</span>
                </li>
              ))}
            </ol>
          ) : null}

          <button type="button" className="tc-how" onClick={() => setGuide(true)}>
            <Trophy size={14} aria-hidden />
            <span>Ideas, tools, builds and likes all earn points. See how points, levels and badges work →</span>
          </button>
        </>
      )}
      {guide ? <PointsGuide points={myPoints} onClose={closeGuide} /> : null}
    </section>
  );
}
